import { createSelector } from 'reselect';
import { AppState } from '../../model';
import { getSegmentOption } from '../segment-options/selectors';
import {
  AirFareOption,
  AirSegmentDetails,
  CarOption,
  CarSegmentDetails,
  CarType,
  FlightOption,
  HotelOption,
  HotelRateOption,
  HotelSegmentDetails,
  OptionId,
  OptionTypes,
} from './../../../models/itinerary';
import { getSegmentType } from './../itinerary/selectors';

/* -------------------------------------------------------------------------- */
/*                                Selected rate                               */
/* -------------------------------------------------------------------------- */

/**
 * Get selected rate
 */
export const getSelected = (state: AppState) => state.selectedRate.rate;

/* -------------------------------------------------------------------------- */
/*                              Get list of rates                             */
/* -------------------------------------------------------------------------- */

const getAirRates = (segmentId: number, optionId: OptionId) =>
  createSelector(
    getSegmentOption(segmentId, optionId),
    option => airRates(option as AirSegmentDetails),
  );

const getHotelRates = (segmentId: number, optionId: OptionId) =>
  createSelector(
    getSegmentOption(segmentId, optionId),
    option => hotelRates(option as HotelSegmentDetails),
  );

const getCarRates = (segmentId: number, optionId: OptionId) =>
  createSelector(
    getSegmentOption(segmentId, optionId),
    option => carRates(option as CarSegmentDetails),
  );

/* -------------------------------------------------------------------------- */
/*                                Default rates                               */
/* -------------------------------------------------------------------------- */

/**
 * Determine default rate for a segment option
 */
const getDefaultRate = (segmentId: number, optionId: OptionId) => (
  state: AppState,
) => {
  const type = getSegmentType(segmentId)(state);

  switch (type) {
    case 'Hotel':
      return getDefaultHotelRate(segmentId, optionId)(state);
    case 'Car':
      return getDefaultCarRate(segmentId, optionId)(state);
    case 'Air':
    default:
      return getDefaultAirRate(segmentId, optionId)(state);
  }
};

const getDefaultHotelRate = (segmentId: number, optionId: OptionId) =>
  createSelector(
    getHotelRates(segmentId, optionId),
    defaultHotelRate,
  );

const getDefaultCarRate = (segmentId: number, optionId: OptionId) =>
  createSelector(
    getCarRates(segmentId, optionId),
    defaultCarRate,
  );

const getDefaultAirRate = (segmentId: number, optionId: OptionId) =>
  createSelector(
    getAirRates(segmentId, optionId),
    defaultAirRate,
  );

export const createDefaultRateOption = (
  segmentId: number,
  optionId: OptionId,
) =>
  createSelector(
    getSegmentType(segmentId),
    getDefaultRate(segmentId, optionId),
    (type, defaultRate): OptionTypes => {
      switch (type) {
        case 'Hotel':
          return createHotelRateOption(optionId, defaultRate);
        case 'Car':
          return createCarRateOption(optionId, defaultRate);
        case 'Air':
        default:
          return createAirRateOption(Number(optionId), defaultRate);
      }
    },
  );

const createHotelRateOption = (
  hotelId: number | string,
  rateId: number,
): HotelOption => ({
  rateId,
  hotelId: Number(hotelId),
});

const createCarRateOption = (
  supplierCode: string,
  carId: number,
): CarOption => ({
  carId,
  supplierCode,
});

const createAirRateOption = (
  flightId: number,
  fareId: number,
): FlightOption => ({
  fareId,
  flightId,
});

/* -------------------------------------------------------------------------- */
/*                              Recommended rates                             */
/* -------------------------------------------------------------------------- */

/**
 * Determine recommended air rate (AKA fare) from list of air rates
 */
const getRecommendedAirRate = (rates: AirFareOption[]) => {
  return rates.find(({ recommended }) => Boolean(recommended));
};

/**
 * Determine recommended hotel rate from list of hotel rates
 */
const getRecommendedHotelRate = (rates: HotelRateOption[]) => {
  return rates.find(({ recommended }) => Boolean(recommended));
};

/**
 * Determine recommended car rate (AKA carId) from list of car rates
 */
const getRecommendedCarRate = (rates: CarType[]) => {
  return rates.find(({ recommended }) => Boolean(recommended));
};

/* -------------------------------------------------------------------------- */
/*                               Cheapest rates                               */
/* -------------------------------------------------------------------------- */

/**
 * Determine cheapest air rate (AKA fare) from list of air rates
 */
const getCheapestAirRate = (rates: AirFareOption[]): AirFareOption => {
  return sortAirRatesByPrice(rates)[0];
};

/**
 * Determine cheapest hotel rate from list of hotel rates
 */
const getCheapestHotelRate = (rates: HotelRateOption[]): HotelRateOption => {
  return sortHotelRatesByPrice(rates)[0];
};

/**
 * Determine cheapest car rate (AKA carId) from list of car rates
 */
const getCheapestCarRate = (rates: CarType[]): CarType => {
  return sortCarRatesByPrice(rates)[0];
};

/* -------------------------------------------------------------------------- */
/*                       Sort rates by price (ascending)                      */
/* -------------------------------------------------------------------------- */

/**
 * Sort list of air rates (AKA fares) by price (ascending)
 */
const sortAirRatesByPrice = (rates: AirFareOption[]): AirFareOption[] => {
  return rates.sort((a, b) => a.totalCost - b.totalCost);
};

/**
 * Sort list of hotel rates by price (ascending)
 */
const sortHotelRatesByPrice = (rates: HotelRateOption[]): HotelRateOption[] => {
  return rates.sort((a, b) => a.cost - b.cost);
};

/**
 * Sort list of car rates (AKA carId) by price (ascending)
 */
const sortCarRatesByPrice = (rates: CarType[]): CarType[] => {
  return rates.sort((a, b) => a.totalAmount - b.totalAmount);
};

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

export const airRates = (option: AirSegmentDetails): AirFareOption[] => {
  return option.fareOptions;
};

export const hotelRates = (option: HotelSegmentDetails): HotelRateOption[] => {
  return option.rates;
};

export const carRates = (option: CarSegmentDetails): CarType[] => {
  return option.carTypes;
};

/**
 * Determine default air rate
 */
const defaultAirRate = (rates: AirFareOption[]) => {
  const { fareId } = getRecommendedAirRate(rates) || getCheapestAirRate(rates);

  return fareId;
};

/**
 * Determine default hotel rate
 */
const defaultHotelRate = (rates: HotelRateOption[]) => {
  const { rateId } =
    getRecommendedHotelRate(rates) || getCheapestHotelRate(rates);

  return rateId;
};

/**
 * Determine default car rate
 */
const defaultCarRate = (rates: CarType[]) => {
  const { carId } = getRecommendedCarRate(rates) || getCheapestCarRate(rates);

  return carId;
};
